import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import logo from "@/assets/logo.png";

export default function Register() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "", address: "", pincode: "", is_gst: false, gstin: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Submitted! You'll be able to log in once admin approves your account.");
      nav(`/login`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img src={logo} alt="Pranav Anand Enterprises" className="h-14 w-auto object-contain"/>
            <span className="font-heading font-semibold text-base" style={{color:"#0e5a32"}}>Pranav Anand Enterprises</span>
          </Link>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mb-2" data-testid="register-title">Create account</h1>
          <p className="text-sm text-gray-500 mb-6">Fill your details — admin will review and share a 4-digit activation code.</p>
          <form onSubmit={onSubmit} className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-gray-600">Full name</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm mt-1" data-testid="register-name"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-600">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm mt-1" data-testid="register-email"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-600">Mobile</Label>
              <Input required value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 98xxxxxxxx" className="rounded-sm mt-1" data-testid="register-mobile"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-600">Address</Label>
              <Textarea required value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-sm mt-1" data-testid="register-address"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-600">Pincode (6 digits)</Label>
              <Input required minLength={6} maxLength={6} pattern="[0-9]{6}" value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} placeholder="400001" className="rounded-sm mt-1" data-testid="register-pincode"/></div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">GST status</Label>
              <div className="flex gap-4 mt-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!form.is_gst} onChange={() => set("is_gst", false)} data-testid="register-non-gst"/> Non-GST
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={form.is_gst} onChange={() => set("is_gst", true)} data-testid="register-gst"/> GST registered
                </label>
              </div>
            </div>
            {form.is_gst && (
              <div><Label className="text-xs uppercase tracking-wider text-gray-600">GSTIN (15 chars)</Label>
                <Input required value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" maxLength={15} className="rounded-sm mt-1 uppercase" data-testid="register-gstin"/></div>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-gray-600">Password (min 6)</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="rounded-sm mt-1" data-testid="register-password"/></div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#002fa7] hover:bg-[#00227b] rounded-sm mt-2" data-testid="register-submit">
              {submitting ? "Submitting…" : "Submit for approval"}
            </Button>
          </form>
          <p className="text-sm text-gray-500 mt-4">
            Already approved? <Link to="/login" className="text-[#002fa7] font-medium hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden md:block relative">
        <img src="https://images.pexels.com/photos/9845176/pexels-photo-9845176.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{background:"rgba(14,90,50,0.7)"}}/>
      </div>
    </div>
  );
}
